/**
 * iter110n · CEO 2026-09-23 · Bouton 1-clic « Rupture période d'essai »
 * sur la fiche candidat.
 *
 * Workflow :
 *  1. Clic sur le bouton rouge visible en haut de la carte contrat
 *  2. Modale de preview : lettre HTML A4, destinataire, boîte d'envoi,
 *     objet, date effective, préavis
 *  3. Bouton "Envoyer maintenant" → POST /candidates/{id}/end-trial/send
 *  4. Toast succès + fermeture modale
 *
 * Design : rouge/orange Shariti, iframe pour la lettre, badges info,
 * animation d'ouverture douce.
 */
import { useCallback, useEffect, useState } from "react";
import {
    Ban, X, Mail, Calendar, FileText, Send, Loader2, CheckCircle2,
    Inbox, Fingerprint, Pencil, RotateCcw, Save,
} from "lucide-react";
import { toast } from "sonner";

import { api } from "../../lib/api";
import { useAuth } from "../../lib/auth";


export default function EndTrialButton({ candidate, onSent, compact = false }) {
    const { user } = useAuth();
    const canEnd = !!(
        user?.role === "admin" ||
        (user?.additional_roles || []).includes("admin") ||
        (user?.role === "coordinateur" && user?.can_manage_contracts) ||
        ((user?.additional_roles || []).includes("coordinateur") && user?.can_manage_contracts)
    );

    const [open, setOpen] = useState(false);
    const [loading, setLoading] = useState(false);
    const [sending, setSending] = useState(false);
    const [preview, setPreview] = useState(null);
    const [error, setError] = useState(null);
    const [confirm, setConfirm] = useState(false);
    const [receipt, setReceipt] = useState(null);

    // iter110n · CEO 2026-09-24 · Édition libre AVANT envoi. `overrides`
    // porte uniquement les champs modifiés par le RH (envoyés en POST).
    const [editing, setEditing] = useState(false);
    const [overrides, setOverrides] = useState({});
    // Le brouillon local du formulaire (avant apply)
    const [draft, setDraft] = useState({});

    const loadPreview = useCallback(async (ovr = null) => {
        setLoading(true);
        setError(null);
        try {
            const url = `/candidates/${candidate.id}/end-trial/preview`;
            const { data } = ovr && Object.keys(ovr).length > 0
                ? await api.post(url, { overrides: ovr })
                : await api.get(url);
            setPreview(data);
        } catch (e) {
            const msg = e?.response?.data?.detail || "Impossible de charger l'aperçu";
            setError(msg);
        } finally {
            setLoading(false);
        }
    }, [candidate.id]);

    useEffect(() => {
        if (open) loadPreview();
        else {
            setPreview(null);
            setError(null);
            setConfirm(false);
            setReceipt(null);
            setEditing(false);
            setOverrides({});
            setDraft({});
        }
    }, [open, loadPreview]);

    // Ouvre l'éditeur avec les valeurs actuelles pré-remplies
    const openEditor = useCallback(() => {
        if (!preview) return;
        setDraft({
            candidate_email: preview.recipient || "",
            cc_str: (preview.cc || []).join(", "),
            civilite: preview.civilite || "",
            first_name: preview.first_name || "",
            last_name: preview.last_name || "",
            subject: preview.subject || "",
            role_label: preview.role_label || "",
            contract_type_label: preview.contract_type_label || "",
            contract_signed_fr: preview.contract_signed_fr || "",
            start_date_fr: preview.start_date_fr || "",
            trial_period_label: preview.trial_period_label || "",
            effective_date_iso: preview.effective_date_iso || "",
            employer_legal_name: preview.employer_legal_name || "",
            employer_legal_form: preview.employer_legal_form || "",
            employer_capital: preview.employer_capital || "",
            employer_rcs: preview.employer_rcs || "",
            employer_address: preview.employer_address || "",
            employer_represented_by: preview.employer_represented_by || "",
        });
        setEditing(true);
    }, [preview]);

    const applyDraft = useCallback(async () => {
        // On construit les overrides à partir du draft — exclut cc_str
        // qui est reconverti en list[str].
        const { cc_str, ...rest } = draft;
        const cc = cc_str
            ? cc_str.split(",").map((e) => e.trim()).filter((e) => e && e.includes("@"))
            : [];
        const next = { ...rest, cc };
        // Enlève les champs vides (ne veut pas casser les defaults backend)
        const cleaned = Object.fromEntries(
            Object.entries(next).filter(([k, v]) => {
                if (k === "cc") return true; // [] est une intention valide
                return v !== "" && v !== null && v !== undefined;
            }),
        );
        setOverrides(cleaned);
        setEditing(false);
        await loadPreview(cleaned);
    }, [draft, loadPreview]);

    const resetOverrides = useCallback(async () => {
        setOverrides({});
        setEditing(false);
        setDraft({});
        await loadPreview(null);
    }, [loadPreview]);

    const handleSend = useCallback(async () => {
        if (!confirm) {
            setConfirm(true);
            return;
        }
        setSending(true);
        try {
            const body = { dry_run: false };
            if (Object.keys(overrides).length > 0) body.overrides = overrides;
            const { data } = await api.post(
                `/candidates/${candidate.id}/end-trial/send`,
                body,
            );
            toast.success(
                `Lettre envoyée à ${data.recipient} · Fin effective ${data.effective_date_fr}`,
            );
            setReceipt(data);
            onSent?.();
        } catch (e) {
            const msg = e?.response?.data?.detail || "Échec de l'envoi";
            toast.error(msg);
        } finally {
            setSending(false);
        }
    }, [candidate.id, confirm, onSent, overrides]);

    if (!canEnd) return null;

    // iter110n · CEO 2026-09-24 · Option C. Si le candidat est DÉJÀ en
    // rupture (audit `trial_period_terminated` journalisé), on affiche
    // un badge d'état non cliquable. Résout la confusion signalée par
    // la chargée des ops (WhatsApp Tom Angeli).
    if (candidate?.trial_terminated_at) {
        const day = _formatShortFR(candidate.trial_terminated_at);
        const by = candidate.trial_terminated_by_name || "—";
        const effective = candidate.trial_terminated_effective_date;
        const effectiveDay = effective ? _formatShortFR(effective) : null;
        const title = effectiveDay
            ? `Rupture actée le ${day} par ${by} · fin de contrat au ${effectiveDay}`
            : `Rupture actée le ${day} par ${by}`;
        return (
            <div
                data-testid={`trial-ended-badge-${candidate.id}`}
                title={title}
                className={
                    compact
                        ? "inline-flex items-center justify-center gap-1 w-full rounded-full border-2 border-red-300 bg-red-100 text-red-900 px-2 py-1 text-[10px] font-black uppercase tracking-widest cursor-help"
                        : "inline-flex items-center gap-2 rounded-full border-2 border-red-300 bg-red-100 text-red-900 px-4 py-2 text-[11px] font-black uppercase tracking-widest cursor-help"
                }
            >
                <Ban className={compact ? "h-3 w-3" : "h-3.5 w-3.5"} />
                <span className="truncate">
                    Rompu {day} · {by}
                </span>
            </div>
        );
    }

    return (
        <>
            <button
                type="button"
                onClick={(e) => {
                    e.stopPropagation();
                    setOpen(true);
                }}
                data-testid={`end-trial-open-btn-${candidate?.id || "std"}`}
                className={
                    compact
                        ? "inline-flex items-center justify-center gap-1 w-full rounded-full border border-red-200 bg-red-50 text-red-700 px-2 py-1 text-[10px] font-black uppercase tracking-wider hover:bg-red-100 hover:border-red-300 transition"
                        : "inline-flex items-center gap-2 rounded-full border-2 border-red-200 bg-red-50 text-red-700 px-4 py-2 text-[11px] font-black uppercase tracking-widest hover:bg-red-100 hover:border-red-300 hover:shadow-md transition"
                }
                title="Rompre la période d'essai — 1 clic, avec aperçu avant envoi"
            >
                <Ban className={compact ? "h-3 w-3" : "h-3.5 w-3.5"} />
                {compact ? "Rompre l'essai" : "Rompre la période d'essai"}
            </button>

            {open && (
                <div
                    className="fixed inset-0 z-50 bg-slate-900/60 backdrop-blur-sm flex items-center justify-center p-4"
                    data-testid="end-trial-modal"
                    onClick={() => !sending && setOpen(false)}
                >
                    <div
                        className="bg-white rounded-2xl shadow-2xl max-w-4xl w-full max-h-[92vh] overflow-hidden flex flex-col"
                        onClick={(e) => e.stopPropagation()}
                    >
                        {/* Header */}
                        <div className="flex items-center justify-between px-6 py-4 border-b border-slate-200 shrink-0">
                            <div className="flex items-center gap-3">
                                <div className="rounded-full bg-red-100 p-2">
                                    <Ban className="h-5 w-5 text-red-700" />
                                </div>
                                <div>
                                    <div className="text-[10px] font-black uppercase tracking-widest text-red-700">
                                        Rupture période d'essai
                                    </div>
                                    <div className="text-lg font-black text-shariti-black">
                                        {receipt ? "Accusé d'envoi" : "Aperçu avant envoi"}
                                    </div>
                                </div>
                                {Object.keys(overrides).length > 0 && !receipt && !editing && (
                                    <span
                                        className="ml-2 rounded-full bg-amber-100 border border-amber-300 text-amber-800 px-2 py-0.5 text-[9px] font-black uppercase tracking-wider"
                                        data-testid="end-trial-overrides-badge"
                                    >
                                        Modifié
                                    </span>
                                )}
                            </div>
                            <div className="flex items-center gap-2">
                                {preview && !receipt && !editing && (
                                    <>
                                        <button
                                            onClick={openEditor}
                                            data-testid="end-trial-edit-btn"
                                            className="inline-flex items-center gap-1.5 rounded-full border border-slate-300 bg-white text-slate-700 px-3 py-1.5 text-[10px] font-black uppercase tracking-widest hover:bg-slate-50 hover:border-slate-400 transition"
                                            title="Modifier destinataire, dates, adresse, objet…"
                                        >
                                            <Pencil className="h-3 w-3" />
                                            Modifier
                                        </button>
                                        {Object.keys(overrides).length > 0 && (
                                            <button
                                                onClick={resetOverrides}
                                                data-testid="end-trial-reset-btn"
                                                className="inline-flex items-center gap-1.5 rounded-full border border-slate-200 bg-white text-slate-500 px-2.5 py-1.5 text-[10px] font-bold hover:text-slate-800 transition"
                                                title="Rétablir les valeurs du contrat"
                                            >
                                                <RotateCcw className="h-3 w-3" />
                                                Rétablir
                                            </button>
                                        )}
                                    </>
                                )}
                                <button
                                    onClick={() => setOpen(false)}
                                    disabled={sending}
                                    data-testid="end-trial-close-btn"
                                    className="p-2 rounded-full hover:bg-slate-100 disabled:opacity-40"
                                >
                                    <X className="h-4 w-4" />
                                </button>
                            </div>
                        </div>

                        {/* Body */}
                        <div className="flex-1 overflow-y-auto p-6">
                            {loading && (
                                <div className="flex items-center gap-2 justify-center py-16 text-slate-500">
                                    <Loader2 className="h-4 w-4 animate-spin" />
                                    Chargement de l'aperçu…
                                </div>
                            )}
                            {error && (
                                <div
                                    className="rounded-xl border border-red-200 bg-red-50 p-6 text-red-800 text-sm"
                                    data-testid="end-trial-error"
                                >
                                    <div className="font-black text-[11px] uppercase tracking-widest mb-2">
                                        Impossible d'envoyer
                                    </div>
                                    {error}
                                </div>
                            )}
                            {receipt && !loading && (
                                <ReceiptView receipt={receipt} onClose={() => setOpen(false)} />
                            )}
                            {editing && !loading && !receipt && (
                                <EditPanel
                                    draft={draft}
                                    setDraft={setDraft}
                                    onCancel={() => setEditing(false)}
                                    onApply={applyDraft}
                                />
                            )}
                            {preview && !loading && !receipt && !editing && (
                                <>
                                    {/* Meta blocks — recipient, from, cc, effective date */}
                                    <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mb-3">
                                        <MetaBox
                                            icon={Mail}
                                            label="Destinataire"
                                            value={preview.recipient}
                                            sub={preview.recipient_name}
                                            testid="end-trial-recipient"
                                        />
                                        <MetaBox
                                            icon={Mail}
                                            label="Depuis"
                                            value={preview.from_email}
                                            testid="end-trial-from"
                                        />
                                    </div>
                                    <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mb-5">
                                        {(() => {
                                            const ccList = preview.cc || [];
                                            const bccList = preview.bcc || [];
                                            const hasBcc = bccList.length > 0;
                                            const anyList = hasBcc ? bccList : ccList;
                                            const label = hasBcc
                                                ? "Copie cachée (BCC)"
                                                : (anyList.length > 0 ? "Copie envoyée à" : "Copie");
                                            const value = anyList.length > 0 ? anyList.join(", ") : "— (aucun)";
                                            const sub = hasBcc
                                                ? "Vous recevrez une copie mais le candidat ne verra pas votre adresse"
                                                : (anyList.length > 0 ? "Vous retrouverez ce mail dans votre boîte de réception" : undefined);
                                            return (
                                                <MetaBox
                                                    icon={Inbox}
                                                    label={label}
                                                    value={value}
                                                    sub={sub}
                                                    accent={hasBcc ? "slate" : "orange"}
                                                    testid="end-trial-cc"
                                                />
                                            );
                                        })()}
                                        <MetaBox
                                            icon={Calendar}
                                            label="Fin effective du contrat"
                                            value={preview.effective_date_fr}
                                            accent="red"
                                            testid="end-trial-effective"
                                        />
                                    </div>

                                    {/* Subject */}
                                    <div className="mb-4">
                                        <div className="text-[9px] font-black uppercase tracking-widest text-slate-500 mb-1">
                                            Objet de l'email
                                        </div>
                                        <div
                                            className="rounded-lg bg-slate-50 border border-slate-200 px-3 py-2 text-[13px] font-bold text-shariti-black"
                                            data-testid="end-trial-subject"
                                        >
                                            {preview.subject}
                                        </div>
                                    </div>

                                    {/* Letter preview (iframe A4) */}
                                    <div>
                                        <div className="flex items-center gap-2 mb-2">
                                            <FileText className="h-3.5 w-3.5 text-slate-500" />
                                            <div className="text-[9px] font-black uppercase tracking-widest text-slate-500">
                                                Lettre officielle (PDF joint)
                                            </div>
                                        </div>
                                        <iframe
                                            title="Aperçu lettre rupture"
                                            srcDoc={preview.letter_html}
                                            data-testid="end-trial-letter-iframe"
                                            className="w-full h-[520px] rounded-xl border-2 border-slate-200 bg-white shadow-inner"
                                        />
                                    </div>
                                </>
                            )}
                        </div>

                        {/* Footer actions */}
                        <div className="px-6 py-4 border-t border-slate-200 flex items-center justify-between gap-3 bg-slate-50 shrink-0">
                            {receipt ? (
                                <>
                                    <div className="text-[11px] text-slate-500 font-medium">
                                        Vous pouvez fermer cette fenêtre — la trace est enregistrée.
                                    </div>
                                    <button
                                        onClick={() => setOpen(false)}
                                        data-testid="end-trial-done-btn"
                                        className="inline-flex items-center gap-2 rounded-full bg-emerald-600 text-white px-5 py-2.5 text-[11px] font-black uppercase tracking-widest hover:bg-emerald-700 transition shadow-md"
                                    >
                                        <CheckCircle2 className="h-3.5 w-3.5" /> Terminé
                                    </button>
                                </>
                            ) : editing ? (
                                <>
                                    <button
                                        onClick={() => setEditing(false)}
                                        data-testid="end-trial-edit-cancel-btn"
                                        className="text-[11px] font-bold text-slate-600 hover:text-slate-900 transition"
                                    >
                                        Annuler les modifications
                                    </button>
                                    <button
                                        onClick={applyDraft}
                                        data-testid="end-trial-edit-apply-btn"
                                        className="inline-flex items-center gap-2 rounded-full bg-orange-600 text-white px-5 py-2.5 text-[11px] font-black uppercase tracking-widest hover:bg-orange-700 transition shadow-md"
                                    >
                                        <Save className="h-3.5 w-3.5" /> Actualiser l'aperçu
                                    </button>
                                </>
                            ) : (
                                <>
                                    <button
                                        onClick={() => setOpen(false)}
                                        disabled={sending}
                                        data-testid="end-trial-cancel-btn"
                                        className="text-[11px] font-bold text-slate-600 hover:text-slate-900 transition disabled:opacity-40"
                                    >
                                        Annuler
                                    </button>
                                    <button
                                        onClick={handleSend}
                                        disabled={!preview || sending || !!error}
                                        data-testid="end-trial-send-btn"
                                        className={`inline-flex items-center gap-2 rounded-full px-5 py-2.5 text-[11px] font-black uppercase tracking-widest transition shadow-md ${
                                            confirm
                                                ? "bg-red-700 text-white hover:bg-red-800"
                                                : "bg-red-600 text-white hover:bg-red-700"
                                        } disabled:opacity-40 disabled:cursor-not-allowed`}
                                    >
                                        {sending ? (
                                            <><Loader2 className="h-3.5 w-3.5 animate-spin" /> Envoi…</>
                                        ) : confirm ? (
                                            <><Send className="h-3.5 w-3.5" /> Confirmer l'envoi</>
                                        ) : (
                                            <><Send className="h-3.5 w-3.5" /> Envoyer maintenant</>
                                        )}
                                    </button>
                                </>
                            )}
                        </div>
                    </div>
                </div>
            )}
        </>
    );
}


function MetaBox({ icon: Icon, label, value, sub, accent, testid }) {
    const accents = {
        orange: "border-orange-200 bg-orange-50",
        red: "border-red-200 bg-red-50",
        slate: "border-slate-300 bg-slate-100",
    };
    return (
        <div
            className={`rounded-xl border p-3 ${accents[accent] || "border-slate-200 bg-slate-50"}`}
            data-testid={testid}
        >
            <div className="flex items-center gap-1.5 mb-1">
                <Icon className="h-3 w-3 text-slate-500" />
                <div className="text-[8px] font-black uppercase tracking-widest text-slate-500">
                    {label}
                </div>
            </div>
            <div className="text-[12px] font-bold text-shariti-black break-words">
                {value}
            </div>
            {sub && (
                <div className="text-[10px] text-slate-500 mt-0.5">{sub}</div>
            )}
        </div>
    );
}


/**
 * iter110n · CEO 2026-09-23 · Vue « accusé d'envoi » affichée après clic
 * sur « Confirmer l'envoi ». Reproduit l'esprit d'un accusé de réception
 * mail : destinataire, copie, réf. message provider, date, PDF (taille +
 * SHA-256). Objectif : le RH voit clairement CE qui a été envoyé et A QUI.
 */
function ReceiptView({ receipt }) {
    const sentAtDisplay = receipt?.sent_at
        ? new Date(receipt.sent_at).toLocaleString("fr-FR", {
              day: "2-digit", month: "long", year: "numeric",
              hour: "2-digit", minute: "2-digit",
          })
        : "à l'instant";
    const pdfKb = receipt?.pdf_size_bytes
        ? `${Math.round(receipt.pdf_size_bytes / 1024)} Ko`
        : "—";
    const provider = receipt?.provider || "—";
    const providerLabel = {
        "resend": "Resend (HTTPS)",
        "infomaniak-smtp": "Infomaniak SMTP",
        "preview-dry-run": "Aperçu — aucun envoi réel",
        "dry-run": "Dry-run — aucun envoi réel",
        "dedupe": "Dédupliqué — mail identique déjà envoyé",
        "mock": "Mock (transport non configuré)",
    }[provider] || provider;
    const isDryRun = provider === "preview-dry-run" || provider === "dry-run";
    const cc = Array.isArray(receipt?.cc) ? receipt.cc : [];

    return (
        <div className="max-w-2xl mx-auto" data-testid="end-trial-receipt-view">
            <div className="flex items-start gap-3 rounded-2xl bg-emerald-50 border-2 border-emerald-200 p-5 mb-5">
                <div className="rounded-full bg-emerald-600 p-2 shrink-0">
                    <CheckCircle2 className="h-5 w-5 text-white" />
                </div>
                <div>
                    <div className="text-[10px] font-black uppercase tracking-widest text-emerald-800">
                        Lettre envoyée
                    </div>
                    <div className="text-lg font-black text-emerald-900 leading-tight mt-0.5">
                        Accusé d'envoi
                    </div>
                    <div className="text-[11px] text-emerald-800 mt-1">
                        Envoyée le <strong>{sentAtDisplay}</strong>
                        {isDryRun && (
                            <span className="ml-2 rounded-full bg-amber-200 text-amber-900 px-2 py-0.5 text-[9px] font-black uppercase tracking-wider">
                                Preview
                            </span>
                        )}
                    </div>
                </div>
            </div>

            <div className="space-y-3">
                <ReceiptRow
                    icon={Mail}
                    label="Destinataire"
                    value={receipt.recipient}
                    testid="end-trial-receipt-to"
                />
                <ReceiptRow
                    icon={Inbox}
                    label={cc.length > 0 ? "En copie (vous)" : "En copie"}
                    value={
                        cc.length > 0
                            ? cc.join(", ")
                            : "— (aucune copie)"
                    }
                    sub={
                        cc.length > 0
                            ? "Vous retrouverez ce mail dans votre boîte de réception."
                            : null
                    }
                    accent="orange"
                    testid="end-trial-receipt-cc"
                />
                <ReceiptRow
                    icon={Mail}
                    label="Depuis"
                    value={receipt.from_email}
                    testid="end-trial-receipt-from"
                />
                <ReceiptRow
                    icon={FileText}
                    label="Objet"
                    value={receipt.subject}
                    testid="end-trial-receipt-subject"
                />
                <ReceiptRow
                    icon={Calendar}
                    label="Fin effective du contrat"
                    value={receipt.effective_date_fr}
                    accent="red"
                    testid="end-trial-receipt-effective"
                />
                <ReceiptRow
                    icon={FileText}
                    label="Pièce jointe PDF"
                    value={`Lettre officielle · ${pdfKb}`}
                    sub={
                        receipt.pdf_sha256
                            ? `SHA-256 : ${receipt.pdf_sha256.slice(0, 24)}…`
                            : null
                    }
                    testid="end-trial-receipt-pdf"
                />
                <ReceiptRow
                    icon={Fingerprint}
                    label="Fournisseur / Réf. message"
                    value={providerLabel}
                    sub={receipt.external_id || "—"}
                    testid="end-trial-receipt-provider"
                />
            </div>

            {isDryRun && (
                <div className="mt-5 rounded-xl border border-amber-200 bg-amber-50 p-3 text-[11px] text-amber-800">
                    <strong>Mode preview :</strong> aucun email n'a réellement été
                    envoyé (garde-fou dev/preview). En production, le mail partira
                    depuis <code>{receipt.from_email}</code> vers <code>{receipt.recipient}</code>
                    {cc.length > 0 && <> avec <code>{cc.join(", ")}</code> en copie</>}.
                </div>
            )}
        </div>
    );
}


function ReceiptRow({ icon: Icon, label, value, sub, accent, testid }) {
    const accents = {
        orange: "border-orange-200 bg-orange-50",
        red: "border-red-200 bg-red-50",
    };
    return (
        <div
            className={`flex items-start gap-3 rounded-xl border px-4 py-3 ${accents[accent] || "border-slate-200 bg-white"}`}
            data-testid={testid}
        >
            <Icon className="h-4 w-4 text-slate-500 shrink-0 mt-0.5" />
            <div className="flex-1 min-w-0">
                <div className="text-[9px] font-black uppercase tracking-widest text-slate-500 mb-0.5">
                    {label}
                </div>
                <div className="text-[13px] font-bold text-shariti-black break-words">
                    {value}
                </div>
                {sub && (
                    <div className="text-[10.5px] text-slate-500 mt-0.5 break-words">
                        {sub}
                    </div>
                )}
            </div>
        </div>
    );
}



/**
 * iter110n · CEO 2026-09-24 · Panneau d'édition ouvert au clic sur le
 * crayon. Le RH peut modifier destinataire/dates/adresse/rôle/civilité/
 * objet AVANT envoi. Chaque champ est présenté dans une section groupée.
 * Cliquer « Actualiser l'aperçu » applique les overrides et re-render la
 * lettre côté serveur avec les nouvelles valeurs.
 */
function EditPanel({ draft, setDraft, onCancel, onApply }) {
    const set = (k) => (e) => setDraft({ ...draft, [k]: e.target.value });
    return (
        <div className="max-w-3xl mx-auto space-y-5" data-testid="end-trial-edit-panel">
            <div className="rounded-xl border border-orange-200 bg-orange-50 px-4 py-3 text-[12px] text-orange-900">
                <strong>Édition libre</strong> · Modifiez ce que vous voulez —
                destinataire, dates, adresse employeur, rôle, civilité, objet…
                Les valeurs par défaut viennent strictement du contrat signé.
                Cliquez <em>Actualiser l'aperçu</em> pour re-rendre la lettre.
            </div>

            <Section title="Destinataire">
                <Field label="Email destinataire" testid="edit-candidate-email">
                    <input
                        type="email"
                        value={draft.candidate_email || ""}
                        onChange={set("candidate_email")}
                        className={inputCls}
                    />
                </Field>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                    <Field label="Civilité" testid="edit-civilite">
                        <select
                            value={draft.civilite || ""}
                            onChange={set("civilite")}
                            className={inputCls}
                        >
                            <option value="">— (auto)</option>
                            <option value="Monsieur">Monsieur</option>
                            <option value="Madame">Madame</option>
                            <option value="Bonjour">Bonjour (neutre)</option>
                        </select>
                    </Field>
                    <Field label="Prénom" testid="edit-first-name">
                        <input
                            value={draft.first_name || ""}
                            onChange={set("first_name")}
                            className={inputCls}
                        />
                    </Field>
                    <Field label="Nom" testid="edit-last-name">
                        <input
                            value={draft.last_name || ""}
                            onChange={set("last_name")}
                            className={inputCls}
                        />
                    </Field>
                </div>
            </Section>

            <Section title="Envoi">
                <Field label="Objet de l'email" testid="edit-subject">
                    <input
                        value={draft.subject || ""}
                        onChange={set("subject")}
                        className={inputCls}
                    />
                </Field>
                <Field
                    label="Copie (CC) — séparés par des virgules"
                    hint="Laissez vide pour désactiver le CC. Par défaut, votre email est mis en copie automatiquement."
                    testid="edit-cc"
                >
                    <input
                        value={draft.cc_str || ""}
                        onChange={(e) => setDraft({ ...draft, cc_str: e.target.value })}
                        placeholder="rh@shariti.com, ceo@shariti.com"
                        className={inputCls}
                    />
                </Field>
            </Section>

            <Section title="Contrat & dates">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                    <Field label="Rôle / poste (label lettre)" testid="edit-role-label">
                        <input
                            value={draft.role_label || ""}
                            onChange={set("role_label")}
                            className={inputCls}
                        />
                    </Field>
                    <Field label="Type de contrat" testid="edit-contract-type">
                        <input
                            value={draft.contract_type_label || ""}
                            onChange={set("contract_type_label")}
                            className={inputCls}
                        />
                    </Field>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                    <Field label="Contrat signé le (FR)" testid="edit-contract-signed">
                        <input
                            value={draft.contract_signed_fr || ""}
                            onChange={set("contract_signed_fr")}
                            className={inputCls}
                            placeholder="ex. 22 septembre 2026"
                        />
                    </Field>
                    <Field label="Date d'embauche (FR)" testid="edit-start-date">
                        <input
                            value={draft.start_date_fr || ""}
                            onChange={set("start_date_fr")}
                            className={inputCls}
                            placeholder="ex. 22 septembre 2026"
                        />
                    </Field>
                    <Field
                        label="Fin effective (date)"
                        hint="Format ISO YYYY-MM-DD — le texte FR se calcule tout seul."
                        testid="edit-effective-date"
                    >
                        <input
                            type="date"
                            value={draft.effective_date_iso || ""}
                            onChange={set("effective_date_iso")}
                            className={inputCls}
                        />
                    </Field>
                </div>
                <Field
                    label="Période d'essai contractuelle (texte lettre)"
                    testid="edit-trial-period"
                >
                    <input
                        value={draft.trial_period_label || ""}
                        onChange={set("trial_period_label")}
                        className={inputCls}
                        placeholder="ex. du 22 septembre 2026 au 05 octobre 2026"
                    />
                </Field>
            </Section>

            <Section title="Employeur">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                    <Field label="Raison sociale" testid="edit-employer-name">
                        <input
                            value={draft.employer_legal_name || ""}
                            onChange={set("employer_legal_name")}
                            className={inputCls}
                        />
                    </Field>
                    <Field label="Forme juridique" testid="edit-employer-form">
                        <input
                            value={draft.employer_legal_form || ""}
                            onChange={set("employer_legal_form")}
                            className={inputCls}
                        />
                    </Field>
                </div>
                <Field label="Adresse du siège social" testid="edit-employer-address">
                    <input
                        value={draft.employer_address || ""}
                        onChange={set("employer_address")}
                        className={inputCls}
                    />
                </Field>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                    <Field label="Capital" testid="edit-employer-capital">
                        <input
                            value={draft.employer_capital || ""}
                            onChange={set("employer_capital")}
                            className={inputCls}
                        />
                    </Field>
                    <Field label="RCS" testid="edit-employer-rcs">
                        <input
                            value={draft.employer_rcs || ""}
                            onChange={set("employer_rcs")}
                            className={inputCls}
                        />
                    </Field>
                    <Field label="Représentée par" testid="edit-employer-rep">
                        <input
                            value={draft.employer_represented_by || ""}
                            onChange={set("employer_represented_by")}
                            className={inputCls}
                        />
                    </Field>
                </div>
            </Section>
        </div>
    );
}


const inputCls = "w-full rounded-lg border border-slate-300 bg-white px-3 py-2 text-[13px] font-medium text-shariti-black focus:outline-none focus:ring-2 focus:ring-orange-400 focus:border-orange-400 transition";


function Section({ title, children }) {
    return (
        <div>
            <div className="text-[10px] font-black uppercase tracking-widest text-orange-700 mb-2">
                {title}
            </div>
            <div className="space-y-3">{children}</div>
        </div>
    );
}


function Field({ label, hint, testid, children }) {
    return (
        <div data-testid={testid}>
            <label className="block text-[10px] font-black uppercase tracking-widest text-slate-500 mb-1">
                {label}
            </label>
            {children}
            {hint && (
                <div className="text-[10px] text-slate-400 mt-1 font-medium">{hint}</div>
            )}
        </div>
    );
}



/**
 * Formatte une date ISO en "DD/MM" pour un affichage compact type badge.
 * Utilisé par le badge d'état "🚫 Rompu 22/09 · Camille V." (iter110n
 * · CEO 2026-09-24).
 */
function _formatShortFR(iso) {
    if (!iso) return "—";
    try {
        const d = new Date(iso);
        if (Number.isNaN(d.getTime())) return "—";
        const day = String(d.getDate()).padStart(2, "0");
        const month = String(d.getMonth() + 1).padStart(2, "0");
        return `${day}/${month}`;
    } catch {
        return "—";
    }
}
